import React from 'react'
import { NavLink } from 'react-router-dom'

// Utils
import { workspaceSettingsPermissions } from 'modules/workspace/utils'

// Constants
import { routes } from '@sketch/modules-common'

// Hooks
import { useIsProfileCreated } from 'modules/community/hooks'

// Components
import { Segment, Navbar } from '@sketch/components'

// Local Components
import {
  WorkspaceSidebarLayout,
  WorkspaceSidebarLayoutExtraProps,
} from '../WorkspaceSidebarLayout'

// Styles
import { ContentWrapper } from './WorkspaceSettingsLayout.styles'

// Types
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

interface WorkspaceSettingsLayoutProps {
  workspace: WorkspaceMinimalFragment
  canUseSso?: boolean
  hideNavbar: boolean
  canRender: {
    generalMenu: boolean
    billingMenu: boolean
    SSOMenu: boolean
    peopleMenu: boolean
    permissionGroups: boolean
  }
}

function WorkspaceSettingsLayout(
  props: React.PropsWithChildren<WorkspaceSettingsLayoutProps> &
    WorkspaceSidebarLayoutExtraProps
) {
  const {
    workspace,
    canRender,
    children,
    hideNavbar,
    NavbarPortal,
    useOverrideLayoutProps,
  } = props

  const { name } = workspace
  const workspaceId = workspace.identifier

  const { isProfileCreated } = useIsProfileCreated(workspaceId)

  const { isAdmin } = workspaceSettingsPermissions({
    workspace,
  })

  useOverrideLayoutProps({
    title: name && `${name} Workspace Settings`,
  })

  return (
    <>
      <NavbarPortal>
        {hideNavbar ? null : (
          <Navbar.Section ml={2} data-testid="workspace-settings-navbar">
            {canRender.peopleMenu && (
              <Segment
                type="text"
                label="People"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_PEOPLE.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {canRender.permissionGroups && (
              <Segment
                type="text"
                label="Groups"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_PERMISSION_GROUPS.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {canRender.generalMenu && (
              <Segment
                type="text"
                label="Settings"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_SETTINGS.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {isProfileCreated && isAdmin && (
              <Segment
                type="text"
                label="Profile"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_PROFILE.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {canRender.billingMenu && (
              <Segment
                type="text"
                label="Billing"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_BILLING.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {canRender.SSOMenu && (
              <Segment
                type="text"
                label="Single Sign-On"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_SSO.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
          </Navbar.Section>
        )}
      </NavbarPortal>

      <ContentWrapper>{children}</ContentWrapper>
    </>
  )
}

const WorkspaceSettingsLayoutWrapper = React.forwardRef<
  HTMLElement,
  React.PropsWithChildren<WorkspaceSettingsLayoutProps>
>(function WorkspaceSettingsLayoutWrapper(props, ref) {
  return (
    <WorkspaceSidebarLayout
      render={layoutProps => (
        <WorkspaceSettingsLayout {...layoutProps} {...props} />
      )}
    />
  )
})

export default WorkspaceSettingsLayoutWrapper
